<template>
  <div class="user-page">
    <!-- ── 页头 ───────────────────────────────────────────── -->
    <div class="page-head">
      <div class="head-text">
        <h1>用户管理</h1>
        <p>学生档案、爱豆余额与拉新关系</p>
      </div>
      <div class="head-actions">
        <el-button type="primary" class="primary-btn" icon="el-icon-plus" @click="handleCreate">
          新增用户
        </el-button>
      </div>
    </div>

    <!-- ── 筛选 ───────────────────────────────────────────── -->
    <div class="filter-bar">
      <el-input
        v-model="listQuery.keyword"
        placeholder="搜索昵称 / 用户名 / 学号"
        clearable
        prefix-icon="el-icon-search"
        class="search-input"
        @keyup.enter.native="handleFilter"
        @clear="handleFilter"
      />
      <el-select v-model="listQuery.schoolId" placeholder="全部学校" clearable class="school-select" @change="handleFilter">
        <el-option v-for="item in schools" :key="item.id" :label="item.name" :value="item.id" />
      </el-select>
      <el-select v-model="listQuery.gender" placeholder="全部性别" clearable class="gender-select" @change="handleFilter">
        <el-option label="男" value="M" />
        <el-option label="女" value="F" />
      </el-select>
      <el-select v-model="listQuery.verifyStatus" placeholder="全部认证" clearable class="verify-select" @change="handleFilter">
        <el-option label="已认证" :value="2" />
        <el-option label="待审核" :value="1" />
        <el-option label="认证驳回" :value="3" />
        <el-option label="未提交" :value="0" />
      </el-select>
      <el-select v-model="listQuery.banned" placeholder="全部状态" clearable class="status-select" @change="handleFilter">
        <el-option label="正常" :value="false" />
        <el-option label="已封禁" :value="true" />
      </el-select>
      <el-button type="primary" class="primary-btn" @click="handleFilter">查询</el-button>
      <el-button class="ghost-btn" @click="resetFilter">重置</el-button>
      <div class="filter-tail">共 <b>{{ total }}</b> 人</div>
    </div>

    <!-- ── 列表 ───────────────────────────────────────────── -->
    <div class="table-card">
      <el-table v-loading="listLoading" :data="list" style="width: 100%" :row-class-name="rowClass" @row-click="handleDetail">
        <el-table-column prop="id" label="ID" width="70" align="center" />
        <el-table-column label="用户" min-width="210">
          <template slot-scope="{ row }">
            <div class="owner-cell">
              <el-avatar :src="row.avatar" :size="36" class="owner-avatar">
                {{ (row.nickname || '用').slice(0, 1) }}
              </el-avatar>
              <div class="owner-info">
                <div class="owner-name">
                  {{ row.nickname || '用户 ' + row.id }}
                  <!-- 认证金点：全站只有"已通过学生认证"能用金色 -->
                  <span v-if="row.isVerified === 1" class="verify-dot" title="已通过学生认证" />
                </div>
                <!-- 年级优先、年龄兜底：与小程序端人物行同一口径，避免同一个用户两端说法不同 -->
                <div class="owner-school">
                  {{ row.gender === 'M' ? '男' : '女' }} · {{ row.gradeLabel || (row.age ? row.age + ' 岁' : '—') }}
                </div>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="学校" min-width="170">
          <template slot-scope="{ row }">
            <span class="cell-main">{{ row.schoolName || '—' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="爱豆" width="130">
          <template slot-scope="{ row }">
            <div class="bean-cell"><b>{{ beanOf(row.balance) }}</b> 爱豆</div>
            <div class="cell-sub">¥{{ Number(row.balance || 0).toFixed(2) }}</div>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="150">
          <template slot-scope="{ row }">
            <span class="status-pill" :class="row.banActive ? 's2' : 's1'">
              <i />{{ row.banActive ? '已封禁' : '正常' }}
            </span>
            <!-- 解封时间放在副行：管理员扫列表时最关心的就是"这人什么时候能回来" -->
            <div v-if="row.banActive" class="cell-sub">
              {{ row.banUntil ? '至 ' + formatDate(row.banUntil) : '永久封禁' }}
            </div>
          </template>
        </el-table-column>
        <el-table-column label="注册时间" width="170">
          <template slot-scope="{ row }">
            <span class="cell-sub">{{ formatDate(row.createdAt) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="230" fixed="right">
          <template slot-scope="{ row }">
            <div class="row-actions">
              <button type="button" class="act act-plain" @click.stop="handleDetail(row)">详情</button>
              <button type="button" class="act act-plain" @click.stop="handleEdit(row)">编辑</button>
              <el-dropdown trigger="click" @command="cmd => handleAction(cmd, row)">
                <button type="button" class="act act-plain act-more" @click.stop>
                  更多<i class="el-icon-arrow-down" />
                </button>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item command="school">
                    <i class="el-icon-school" /> 修改学校
                  </el-dropdown-item>
                  <el-dropdown-item command="reset">
                    <i class="el-icon-key" /> 重置密码
                  </el-dropdown-item>
                  <el-dropdown-item v-if="row.banActive" command="unban">
                    <i class="el-icon-unlock" /> 解除封禁
                  </el-dropdown-item>
                  <el-dropdown-item v-else command="ban">
                    <i class="el-icon-lock" /> 封禁账号
                  </el-dropdown-item>
                  <el-dropdown-item command="delete" divided>
                    <i class="el-icon-delete" /> 删除
                  </el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <div class="pager">
        <el-pagination
          v-show="total > 0"
          background
          :current-page="listQuery.page"
          :page-size="listQuery.size"
          :page-sizes="[10, 20, 50]"
          :total="total"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </div>

    <!-- ══════════════ 用户详情抽屉 ══════════════
         一个人 = 一屏看完。原来拆成 6 个 tab，管理员要点 6 次才能拼出这个人的画像，
         而真正需要一眼看到的（认证没认证、豆还有多少、有没有被封、有多少内容）全被拆散了。 -->
    <el-drawer
      :visible.sync="detailVisible"
      direction="rtl"
      size="720px"
      :with-header="false"
      custom-class="admin-drawer"
      @closed="onDrawerClosed"
    >
      <div v-loading="detailLoading" class="drawer-inner">
          <!-- 抽屉头：只说"这是谁、什么状态" -->
          <div class="drawer-head">
            <div class="dh-left">
              <span class="dh-id">{{ userDetail.nickname || '用户 ' + userDetail.id }}</span>
              <span v-if="userDetail.isVerified === 1" class="status-pill is-solid is-gold">
                <i />已认证
              </span>
              <span class="status-pill is-solid" :class="userDetail.banActive ? 's2' : 's1'">
                <i />{{ userDetail.banActive ? '已封禁' : '正常' }}
              </span>
            </div>
            <button type="button" class="dh-close" @click="detailVisible = false">
              <i class="el-icon-close" />
            </button>
          </div>

        <div class="drawer-body">
          <!-- ── 人物 + 爱豆：这一屏的视觉重心 ───────────────── -->
          <div class="ud-hero">
            <div class="ud-hero-top">
              <el-avatar :size="64" :src="userDetail.avatar" class="ud-avatar">
                {{ (userDetail.nickname || '用').slice(0, 1) }}
              </el-avatar>
              <div class="ud-hero-id">
                <div class="ud-name">
                  {{ userDetail.nickname || '未设置昵称' }}
                  <span v-if="userDetail.isVerified === 1" class="verify-dot" title="已通过学生认证" />
                </div>
                <div class="ud-handle">@{{ userDetail.username }} · ID {{ userDetail.id }}</div>
                <div class="ud-chips">
                  <span class="tag">{{ userDetail.gender === 'M' ? '男' : '女' }}</span>
                  <span v-if="userDetail.gradeLabel" class="tag">{{ userDetail.gradeLabel }}</span>
                  <span class="tag">{{ userDetail.age ? userDetail.age + ' 岁' : '年龄未填' }}</span>
                  <span class="tag">{{ userDetail.schoolName || '未选择学校' }}</span>
                </div>
              </div>
            </div>

            <!-- 爱豆余额：管理端最常核对的一个数，单独占一行放大 -->
            <div class="ud-bean">
              <div class="ud-bean-main">
                <b>{{ beanOf(userDetail.balance) }}</b>
                <em>爱豆</em>
              </div>
              <div class="ud-bean-meta">
                <span>¥{{ Number(userDetail.balance || 0).toFixed(2) }}</span>
                <span v-if="statLoaded">累计获得 {{ beanOf(userStatistics.totalIncome) }}</span>
                <span v-if="statLoaded">累计消耗 {{ beanOf(userStatistics.totalExpense) }}</span>
                <span v-if="statLoaded">累计充值 {{ beanOf(userStatistics.rechargeAmount) }}</span>
              </div>
            </div>
          </div>

          <!-- 对账提示：余额和流水净额对不上，说明有"没走流水"的余额变动。
               这行就是给管理员的答案 —— 与其让他自己拿计算器减一遍，不如直接把差额摆出来。 -->
          <div v-if="hasBalanceGap" class="hint-bar ud-gap">
            <i class="el-icon-warning-outline" />
            流水净额 {{ beanOf(beanNet) }} 爱豆，与当前余额相差
            <b>{{ beanOf(balanceGap) }}</b> 爱豆 —— 存在没走流水的余额变动，对账时注意。
          </div>

          <!-- ── 行为计数 ───────────────────────────────────── -->
          <div class="metric-row">
            <div class="metric">
              <b>{{ statOr(userStatistics.noteCount) }}<em>张</em></b>
              <span>放入纸条</span>
            </div>
            <div class="metric">
              <b>{{ statOr(userStatistics.takenCount) }}<em>张</em></b>
              <span>取出纸条</span>
            </div>
            <div class="metric">
              <b>{{ statOr(userStatistics.momentCount) }}<em>条</em></b>
              <span>心动圈动态</span>
            </div>
            <div class="metric">
              <b>{{ statOr(userStatistics.commentCount) }}<em>条</em></b>
              <span>发表评论</span>
            </div>
            <div class="metric">
              <b>{{ statOr(userStatistics.receivedLikeCount) }}<em>个</em></b>
              <span>收到点赞</span>
            </div>
          </div>

          <!-- ── 封禁：异常状态必须顶在最上面，且把原因摊开 ──── -->
          <div v-if="userDetail.banActive" class="ud-ban">
            <i class="el-icon-warning-outline" />
            <div>
              <b>{{ userDetail.banUntil ? '该账号封禁至 ' + formatDate(userDetail.banUntil) : '该账号已被永久封禁' }}</b>
              <p class="ud-ban-reason">原因：{{ userDetail.banReason || '—' }}</p>
              <p class="ud-ban-meta">
                封禁时间 {{ formatDate(userDetail.banAt) }}
                <template v-if="userDetail.banBy"> · 操作人 {{ userDetail.banBy }}</template>
              </p>
            </div>
          </div>
          <div v-else-if="userDetail.banAt" class="hint-bar">
            该账号曾被封禁（{{ formatDate(userDetail.banAt) }}），当前已解封或封禁已到期。
          </div>

          <!-- ── 基础资料 ───────────────────────────────────── -->
          <div class="section">
            <div class="section-title">基础资料</div>
            <div class="info-grid">
              <div class="info-item">
                <label>性别 / 年级</label>
                <span>
                  {{ userDetail.gender === 'M' ? '男' : '女' }}
                  · {{ userDetail.gradeLabel || (userDetail.age ? userDetail.age + ' 岁' : '未填') }}
                </span>
              </div>
              <div class="info-item">
                <label>学业信息</label>
                <span :class="{ 'is-muted': !userDetail.eduLevel }">
                  {{ eduText }}
                </span>
              </div>
              <div class="info-item">
                <label>学校</label>
                <span :class="{ 'is-muted': !userDetail.schoolName }">{{ userDetail.schoolName || '未选择' }}</span>
              </div>
              <div class="info-item">
                <label>下次可自行改学校</label>
                <span :class="{ 'is-muted': !userDetail.nextSchoolChangeAt }">
                  {{ userDetail.nextSchoolChangeAt ? formatDate(userDetail.nextSchoolChangeAt) : '随时可改' }}
                </span>
              </div>
              <div class="info-item">
                <label>联系方式</label>
                <span :class="{ 'is-muted': !userDetail.contact }">{{ userDetail.contact || '未填写' }}</span>
              </div>
              <div class="info-item">
                <label>邮箱</label>
                <span :class="{ 'is-muted': !userDetail.email }">{{ userDetail.email || '未填写' }}</span>
              </div>
              <div class="info-item">
                <label>注册时间</label>
                <span>{{ formatDate(userDetail.createdAt) }}</span>
              </div>
              <div class="info-item">
                <label>资料完善</label>
                <span>
                  {{ userDetail.isProfileComplete === 1 ? '已完善（可领取新手礼）' : '未完善' }}
                </span>
              </div>
              <div class="info-item is-full">
                <label>个人简介</label>
                <span :class="{ 'is-muted': !userDetail.bio }">{{ userDetail.bio || '未填写' }}</span>
              </div>
            </div>
          </div>

          <!-- ── 学生认证 ───────────────────────────────────── -->
          <div class="section">
            <div class="section-title">学生认证</div>
            <div class="ud-verify">
              <span class="status-pill is-solid" :class="verifyPillClass">
                <i />{{ verifyText }}
              </span>
              <span class="ud-verify-hint">{{ verifyHint }}</span>
            </div>
            <div v-if="userDetail.verifyStatus && userDetail.verifyStatus !== 0" class="info-grid is-tight">
              <div class="info-item">
                <label>学号</label>
                <span>{{ userDetail.verifyStudentNo || '—' }}</span>
              </div>
              <div class="info-item">
                <label>真实姓名</label>
                <span>{{ userDetail.verifyRealName || '—' }}</span>
              </div>
              <div class="info-item">
                <label>提交时间</label>
                <span>{{ formatDate(userDetail.verifySubmittedAt) || '—' }}</span>
              </div>
              <div class="info-item">
                <label>审核时间</label>
                <span>{{ formatDate(userDetail.verifyReviewedAt) || '—' }}</span>
              </div>
              <div v-if="userDetail.verifyRemark" class="info-item is-full">
                <label>驳回原因</label>
                <span>{{ userDetail.verifyRemark }}</span>
              </div>
            </div>
          </div>

          <!-- ── 今日免费额度：回答"我为什么被扣豆了" ─────────── -->
          <div class="section">
            <div class="section-title">今日免费额度</div>
            <div class="ud-quota">
              <div class="ud-quota-item">
                <div class="ud-quota-label">免费取出</div>
                <div class="ud-quota-num">
                  <b>{{ userDetail.todayFreeTakeUsed || 0 }}</b>
                  <em>/ {{ userDetail.todayFreeTakeTotal || 0 }} 次</em>
                </div>
                <div class="ud-quota-note">
                  {{ quotaNote(userDetail.todayFreeTakeUsed, userDetail.todayFreeTakeTotal) }}
                </div>
              </div>
              <div class="ud-quota-item">
                <div class="ud-quota-label">免费投放</div>
                <div class="ud-quota-num">
                  <b>{{ userDetail.todayFreeCreateUsed || 0 }}</b>
                  <em>/ {{ userDetail.todayFreeCreateTotal || 0 }} 次</em>
                </div>
                <div class="ud-quota-note">
                  {{ quotaNote(userDetail.todayFreeCreateUsed, userDetail.todayFreeCreateTotal) }}
                </div>
              </div>
            </div>
            <div class="ud-quota-tip">
              取出上限把「已认证 +1 次」算进去了，与用户端看到的数字同口径；用完之后按配置扣爱豆。
            </div>
          </div>

          <!-- ── 爱豆明细：和用户端是同一张表，可当场对账 ────── -->
          <div class="section">
            <div class="section-title">
              爱豆明细
              <span class="count">{{ userTransactions.length }}</span>
            </div>
            <div v-if="userTransactions.length" class="ud-tx">
              <div v-for="tx in userTransactions" :key="tx.id" class="ud-tx-row">
                <div class="ud-tx-main">
                  <span class="ud-tx-type">{{ txText(tx.type) }}</span>
                  <span class="ud-tx-desc">{{ tx.description || '—' }}</span>
                </div>
                <div class="ud-tx-amount" :class="Number(tx.amount) < 0 ? 'is-out' : 'is-in'">
                  {{ Number(tx.amount) > 0 ? '+' : '' }}{{ beanOf(tx.amount) }}
                </div>
                <div class="ud-tx-time">{{ formatDate(tx.createdAt) }}</div>
              </div>
              <div v-if="txHasMore" class="ud-tx-more">仅显示最近 50 条</div>
            </div>
            <div v-else class="empty-state">
              <i class="el-icon-coin" />
              <p>这个用户还没有爱豆流水</p>
            </div>
          </div>

          <!-- ── 纸条 ───────────────────────────────────────── -->
          <div class="section">
            <div class="section-title">
              放入的纸条
              <span class="count">{{ userNotes.length }}</span>
            </div>
            <div v-if="userNotes.length" class="ud-list">
              <div v-for="n in userNotes.slice(0, 20)" :key="n.id" class="ud-list-row">
                <div class="ud-list-main">
                  <span class="ud-list-text">{{ n.message }}</span>
                </div>
                <div class="ud-list-meta">
                  {{ n.currentTakeCount || 0 }} / {{ n.maxTakeCount }} 人取走 · {{ formatDate(n.createdAt) }}
                </div>
              </div>
              <div v-if="userNotes.length > 20" class="ud-list-more">
                仅显示最近 20 条，共 {{ userNotes.length }} 条
              </div>
            </div>
            <div v-else class="empty-state">
              <i class="el-icon-tickets" />
              <p>还没有放入过纸条</p>
            </div>
          </div>

          <div class="section">
            <div class="section-title">
              取出的纸条
              <span class="count">{{ userTakenNotes.length }}</span>
            </div>
            <div v-if="userTakenNotes.length" class="ud-list">
              <div v-for="t in userTakenNotes.slice(0, 20)" :key="t.id" class="ud-list-row">
                <div class="ud-list-main">
                  <span class="ud-list-text">{{ t.noteContent || '（纸条已删除）' }}</span>
                </div>
                <div class="ud-list-meta">
                  来自 {{ t.ownerNickname || '用户' + t.noteId }} · {{ formatDate(t.createdAt) }}
                </div>
              </div>
              <div v-if="userTakenNotes.length > 20" class="ud-list-more">
                仅显示最近 20 条，共 {{ userTakenNotes.length }} 条
              </div>
            </div>
            <div v-else class="empty-state">
              <i class="el-icon-tickets" />
              <p>还没有取出过纸条</p>
            </div>
          </div>

          <!-- ── 拉新关系：被谁拉的 + 拉了谁，两个方向都要 ───── -->
          <div class="section">
            <div class="section-title">
              拉新关系
              <span class="count">{{ userReferrals.length }}</span>
            </div>
            <div class="ud-ref-line">
              <span class="ud-ref-label">自己的推荐码</span>
              <span class="tag is-mono">{{ userDetail.referralCode || '—' }}</span>
            </div>
            <div class="ud-ref-line">
              <span class="ud-ref-label">由谁推荐</span>
              <span v-if="userDetail.referrerNickname" class="ud-ref-value">
                {{ userDetail.referrerNickname }}
                <span class="ud-ref-id">ID {{ userDetail.referrerId }}</span>
              </span>
              <span v-else class="ud-ref-value is-muted">不是被邀请来的</span>
            </div>

            <div v-if="userReferrals.length" class="ud-list">
              <div v-for="r in userReferrals" :key="r.id" class="ud-list-row is-ref">
                <div class="ud-list-main">
                  <span class="ud-list-text">{{ r.refereeNickname || '用户' + r.refereeId }}</span>
                  <span class="ud-list-meta-inline">{{ r.refereeSchoolName || '—' }} · {{ formatDate(r.createdAt) }}</span>
                </div>
                <div class="ud-list-side">
                  <!-- ⛔ rewardAmount 是「邀请人 + 被邀请人」的合计（平台付出），不是单独给邀请人的 -->
                  <span class="bean-inline" :title="r.referrerReward != null ? '双方合计；其中邀请人 ' + beanOf(r.referrerReward) + ' 豆' : '双方合计'">{{ beanOf(r.rewardAmount) }}</span>
                  <span class="status-pill is-solid" :class="r.isRewarded === 1 ? 's1' : 's0'">
                    <i />{{ r.isRewarded === 1 ? '已发放' : '未发放' }}
                  </span>
                  <button
                    v-if="r.isRewarded !== 1"
                    type="button"
                    class="act act-primary"
                    @click="handleReward(r)"
                  >
                    发放奖励
                  </button>
                </div>
              </div>
            </div>
            <div v-else class="empty-state">
              <i class="el-icon-share" />
              <p>还没有成功邀请过同学</p>
            </div>
          </div>

          <!-- ── 生活照片 ───────────────────────────────────── -->
          <div class="section">
            <div class="section-title">
              生活照片
              <span class="count">{{ lifePhotos.length }}</span>
            </div>
            <div v-if="lifePhotos.length" class="photos-container">
              <div v-for="(photo, index) in lifePhotos" :key="index" class="photo-item">
                <el-image :src="photo" fit="cover" :preview-src-list="lifePhotos" style="width: 100%; height: 160px;" />
              </div>
            </div>
            <div v-else class="empty-state">
              <i class="el-icon-picture-outline" />
              <p>该用户还没有上传生活照片</p>
            </div>
          </div>
        </div>

        <!-- 抽屉底部：动作件收敛在这里，正文只管阅读 -->
        <div class="drawer-foot">
          <el-button size="small" class="ghost-btn" @click="handleEdit(userDetail)">编辑资料</el-button>
          <el-button size="small" class="ghost-btn" @click="openSchoolDialog(userDetail)">代改学校</el-button>
          <el-button size="small" class="ghost-btn" @click="handleResetPassword(userDetail)">重置密码</el-button>
          <div class="foot-spacer" />
          <el-button
            v-if="userDetail.banActive"
            size="small"
            class="ghost-btn"
            @click="handleUnban(userDetail)"
          >
            解除封禁
          </el-button>
          <el-button
            v-else
            size="small"
            class="ghost-btn"
            @click="openBanDialog(userDetail)"
          >
            封禁账号
          </el-button>
          <!-- ⛔ 只有超管能注销用户：这是不可逆动作（连带清理其全部内容与记录），
               后端也做了同样的角色校验，这里隐藏按钮只是不让学校管理员白点一次 -->
          <el-button
            v-if="isSuperAdmin"
            size="small"
            class="danger-btn"
            @click="handleDelete(userDetail)"
          >注销账号</el-button>
        </div>
      </div>
    </el-drawer>

    <!-- ── 新增 / 编辑 ───────────────────────────────────────── -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="500px">
      <el-form ref="form" :model="form" :rules="rules" label-width="80px">
        <el-form-item label="用户名" prop="username">
          <el-input v-model="form.username" :disabled="isEdit" />
        </el-form-item>
        <el-form-item label="昵称" prop="nickname">
          <el-input v-model="form.nickname" />
        </el-form-item>
        <el-form-item v-if="!isEdit" label="密码" prop="password">
          <el-input v-model="form.password" type="password" show-password />
        </el-form-item>
        <el-form-item label="性别" prop="gender">
          <el-radio-group v-model="form.gender">
            <el-radio label="M">男</el-radio>
            <el-radio label="F">女</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="邮箱" prop="email">
          <el-input v-model="form.email" />
        </el-form-item>
        <el-form-item label="出生年月" prop="birthDate">
          <el-date-picker v-model="form.birthDate" type="date" placeholder="选择出生年月" value-format="yyyy-MM-dd" style="width: 100%;" />
        </el-form-item>
        <el-form-item label="学校" prop="schoolId">
          <el-select v-model="form.schoolId" placeholder="请选择学校" style="width: 100%;">
            <el-option v-for="item in schools" :key="item.id" :label="schoolOptionLabel(item)" :value="item.id" />
          </el-select>
          <div v-if="isEdit" class="form-note">
            换学校会一并撤销该用户的学生认证（认证跟学校绑定）。常用的代改入口在列表的「更多 → 修改学校」。
          </div>
        </el-form-item>
        <el-form-item label="联系方式">
          <el-input v-model="form.contact" />
        </el-form-item>

        <el-form-item label="推荐码">
          <el-input v-model="form.referralCode" />
        </el-form-item>
        <el-form-item label="个人简介">
          <el-input v-model="form.bio" type="textarea" :rows="3" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button class="ghost-btn" @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" class="primary-btn" @click="submitForm">确定</el-button>
      </span>
    </el-dialog>

    <!-- ── 代改学校 ───────────────────────────────────────── -->
    <el-dialog title="修改学校" :visible.sync="schoolDialogVisible" width="480px">
      <div class="school-change">
        <div class="school-change__who">
          <span class="school-change__name">{{ schoolChange.nickname }}</span>
          <span class="school-change__id">ID {{ schoolChange.userId }}</span>
        </div>

        <div class="school-change__flow">
          <div class="school-change__side">
            <div class="school-change__label">当前学校</div>
            <div class="school-change__school">{{ schoolChange.fromSchoolName }}</div>
          </div>
          <i class="el-icon-right school-change__arrow" />
          <div class="school-change__side">
            <div class="school-change__label">改为</div>
            <div class="school-change__school" :class="{ 'is-empty': !schoolChange.schoolId }">
              {{ schoolNameById(schoolChange.schoolId) || '未选择' }}
            </div>
          </div>
        </div>

        <el-select v-model="schoolChange.schoolId" placeholder="请选择要改到的学校" style="width: 100%;">
          <el-option
            v-for="item in schools"
            :key="item.id"
            :label="schoolOptionLabel(item)"
            :value="item.id"
            :disabled="item.id === schoolChange.fromSchoolId"
          />
        </el-select>

        <!-- 认证会被撤销这件事必须前置说明，不能等提交完才让管理员发现 -->
        <div v-if="schoolChange.hasVerify" class="school-change__warn">
          <i class="el-icon-warning-outline" />
          <div>
            <b>该用户已通过学生认证，改完会被撤销。</b>
            认证审核的是"是不是这所学校的学生"，换了学校原认证就不成立，
            需要用户在新学校重新提交材料。
          </div>
        </div>

        <div class="school-change__meta">
          <span>用户每年只能自己改一次学校，管理端代改不受此限制，也不占用用户自己的额度。</span>
          <span v-if="schoolChange.nextSchoolChangeAt" class="school-change__next">
            用户下次可自行修改：{{ formatDate(schoolChange.nextSchoolChangeAt) }}
          </span>
        </div>
      </div>
      <span slot="footer">
        <el-button class="ghost-btn" @click="schoolDialogVisible = false">取消</el-button>
        <el-button type="primary" class="primary-btn" @click="submitSchoolChange">确认修改</el-button>
      </span>
    </el-dialog>

    <!-- ── 封禁 ───────────────────────────────────────────── -->
    <el-dialog title="封禁账号" :visible.sync="banDialogVisible" width="480px">
      <div class="ban-box">
        <div class="ban-box__who">
          <span class="ban-box__name">{{ banForm.nickname }}</span>
          <span class="ban-box__id">ID {{ banForm.userId }}</span>
        </div>

        <div class="ban-field">
          <div class="ban-field__label">
            封禁原因
            <span class="ban-field__hint">这段话会原样显示在对方的登录弹窗里，写清楚具体原因</span>
          </div>
          <el-input
            v-model="banForm.reason"
            type="textarea"
            :rows="3"
            maxlength="200"
            show-word-limit
            placeholder="例如：多次发布违规内容，提醒后仍未改正"
          />
        </div>

        <div class="ban-field">
          <div class="ban-field__label">
            封禁时长
            <span class="ban-field__hint">到期自动解封，不需要再手动操作</span>
          </div>
          <div class="chip-row">
            <span
              v-for="opt in banDurationOptions"
              :key="String(opt.value)"
              class="chip"
              :class="{ 'is-on': banForm.durationHours === opt.value }"
              @click="banForm.durationHours = opt.value"
            >{{ opt.label }}</span>
          </div>
          <div v-if="banForm.durationHours !== null" class="custom-dur">
            <el-input-number v-model="banForm.durationHours" :min="1" :max="8760" :step="1" size="small" controls-position="right" />
            <span class="custom-dur__unit">小时</span>
          </div>
        </div>

        <!-- 后果前置：封完会发生什么，在点确认之前就说清楚 -->
        <div class="ban-box__effect">
          <i class="el-icon-warning-outline" />
          <div>
            <b>{{ banEffectText }}</b>
            对方会立刻下线，再次登录时会看到上面这段原因和解封时间。
          </div>
        </div>
      </div>
      <span slot="footer">
        <el-button class="ghost-btn" @click="banDialogVisible = false">取消</el-button>
        <el-button type="primary" class="primary-btn" :loading="banSubmitting" @click="submitBan">确认封禁</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { getUserList, createUser, updateUser, deleteUser, getUserDetail, resetUserPassword, getUserNotes, getUserTakenNotes, getUserStatistics, getUserTransactions, getUserReferrals, updateUserSchool, banUser, unbanUser } from '@/api/user'
import { getSchoolOptions } from '@/api/school'
import { beanNumber } from '@/utils/bean'
import { rewardReferral } from '@/api/referral'

/**
 * 爱豆明细的类型文案。
 * ⛔ 键必须与库里 transactions.type 的**实际取值**对齐，不能照着 ConfigKeys 里的
 *    TX_* 常量抄 —— 那套常量并没有被全部用上，实际写入是另一批字面量：
 *    纸条退款写的是 NOTE_REFUND（不是 TX_REFUND 的 REFUND）、
 *    管理端发邀请奖写的是 REFERRAL_REWARD（不是 TX_REFERRAL 的 REFERRAL）。
 *    抄错的表现是明细里直接冒出英文代码。
 */
const TX_TEXT = {
  ADMIN_RECHARGE: '管理员充值',
  NOTE_POST: '投放纸条',
  NOTE_TAKE: '取出纸条',
  NOTE_REFUND: '纸条驳回退款',
  REFUND: '退款',
  RECHARGE: '充值',
  NEWBIE_GIFT: '新手礼',
  SIGN_IN: '每日签到',
  REFERRAL: '邀请奖励',
  REFERRAL_REWARD: '邀请奖励',
  MOMENT_PIN: '动态置顶'
}

/** 学历层次文案。与后端 constant/EduLevel 一致 */
const EDU_TEXT = { 1: '专科', 2: '本科', 3: '硕士', 4: '博士', 5: '校友' }

export default {
  name: 'User',
  data() {
    return {
      list: [],
      total: 0,
      listLoading: false,
      schools: [],
      listQuery: {
        page: 1,
        size: 10,
        keyword: '',
        schoolId: '',
        gender: '',
        // '' = 不筛选；null 之外的取值走后端 verify_status 过滤
        verifyStatus: '',
        // '' = 不筛选；true = 只看已封禁；false = 只看正常
        banned: ''
      },
      dialogVisible: false,
      dialogTitle: '',
      isEdit: false,
      form: {
        id: null,
        username: '',
        nickname: '',
        password: '',
        gender: 'M',
        email: '',
        birthDate: '',
        schoolId: '',
        contact: '',
        bio: ''
      },
      rules: {
        nickname: [{ required: true, message: '请输入昵称', trigger: 'blur' }],
        password: [{ required: true, message: '请输入密码', trigger: 'blur' }],
        gender: [{ required: true, message: '请选择性别', trigger: 'change' }],
        birthDate: [{ required: true, message: '请选择出生年月', trigger: 'change' }],
        schoolId: [{ required: true, message: '请选择学校', trigger: 'change' }]
      },
      detailVisible: false,
      detailLoading: false,
      userDetail: {},
      userNotes: [],
      userTakenNotes: [],
      userStatistics: {},
      statLoaded: false,
      userReferrals: [],
      userTransactions: [],
      /* 封禁 */
      banDialogVisible: false,
      banSubmitting: false,
      banForm: {
        userId: null,
        nickname: '',
        reason: '',
        // null = 永久。与后端约定一致（durationHours 为空或 <=0 即永久）
        durationHours: 24
      },
      banDurationOptions: [
        { label: '1 天', value: 24 },
        { label: '3 天', value: 72 },
        { label: '7 天', value: 168 },
        { label: '30 天', value: 720 },
        { label: '永久', value: null }
      ],
      /* 代改学校 */
      schoolDialogVisible: false,
      schoolChange: {
        userId: null,
        nickname: '',
        fromSchoolId: null,
        fromSchoolName: '',
        schoolId: '',
        nextSchoolChangeAt: null,
        hasVerify: false
      }
    }
  },
  computed: {
    isSuperAdmin() {
      return this.$store.getters.isSuperAdmin
    },
    lifePhotos() {
      if (!this.userDetail.lifePhotosList) return []
      try {
        let photos = []
        if (Array.isArray(this.userDetail.lifePhotosList)) {
          photos = this.userDetail.lifePhotosList
        } else {
          const str = this.userDetail.lifePhotosList
          if (str.startsWith('[') || str.startsWith('[')) {
            photos = JSON.parse(str)
          } else {
            photos = str.split(',').filter(p => p.trim())
          }
        }
        return photos.map(photo => {
          if (photo.startsWith('http://') || photo.startsWith('https://')) {
            return photo
          }
          return photo.startsWith('/') ? photo : '/' + photo
        })
      } catch (e) {
        console.error('解析生活照片失败:', e)
        return []
      }
    },

    /** 学业信息一行话：本科 · 2023 年入学 */
    eduText() {
      const level = EDU_TEXT[this.userDetail.eduLevel]
      if (!level) return '未填写'
      if (this.userDetail.eduLevel === 5) return '校友（已毕业）'
      return this.userDetail.enrollYear ? `${level} · ${this.userDetail.enrollYear} 年入学` : level
    },

    /** 认证状态药丸：待审=黄、已认证=金（全站只有认证用金）、驳回=红、未提交=灰 */
    verifyPillClass() {
      const s = Number(this.userDetail.verifyStatus || 0)
      if (s === 1) return 's0'
      if (s === 2) return 'is-gold'
      if (s === 3) return 's2'
      return ''
    },
    verifyText() {
      const s = Number(this.userDetail.verifyStatus || 0)
      return ['未提交', '审核中', '已认证', '认证未通过'][s] || '未提交'
    },
    /** 认证不只是一个状态，还决定"他现在能不能用认证特权" —— 直接把影响写出来 */
    verifyHint() {
      const s = Number(this.userDetail.verifyStatus || 0)
      if (s === 2) return '认证有效：资料页显示认证标识，每天多 1 次免费取出'
      if (s === 1) return '正在等待审核，可在「认证审核」页处理'
      if (s === 3) return '认证被驳回，用户可修改材料后重新提交'
      return '未提交认证材料。认证是可选功能，不影响任何其他功能的使用'
    },

    /** 爱豆明细：后端固定最多给 50 条，取满就说明还有更早的没显示 */
    txHasMore() {
      return this.userTransactions.length >= 50
    },

    /** 流水净额 = 累计获得 − 累计消耗（元）。理论值应与余额相等 */
    beanNet() {
      if (!this.statLoaded) return 0
      const income = Number(this.userStatistics.totalIncome || 0)
      const expense = Number(this.userStatistics.totalExpense || 0)
      return income - expense
    },
    /**
     * 余额 − 流水净额。正常为 0；不为 0 就说明有余额变动没走 transactions，
     * 例如管理端「余额充值」只改了 users.balance 而没写流水。
     * 保留到分，避免浮点误差把 0 显示成 1e-14。
     */
    balanceGap() {
      if (!this.statLoaded) return 0
      const gap = Number(this.userDetail.balance || 0) - this.beanNet
      return Number(gap.toFixed(2))
    },
    hasBalanceGap() {
      return this.statLoaded && Math.abs(this.balanceGap) >= 0.01
    },

    /** 封禁时长 → 解封时刻（本地时间文案）；永久封禁返回空串 */
    banUntilPreview() {
      const h = this.banForm.durationHours
      if (h === null || h === undefined || h <= 0) return ''
      return this.formatDate(new Date(Date.now() + h * 3600 * 1000))
    },

    /** 封完会发生什么 —— 在点「确认封禁」之前就摆出来 */
    banEffectText() {
      if (!this.banUntilPreview) return '该账号将被永久封禁。'
      return `该账号将被封禁至 ${this.banUntilPreview}。`
    }
  },
  created() {
    this.fetchData()
    this.fetchSchools()
  },
  methods: {
    fetchData() {
      this.listLoading = true
      // 空字符串会被 axios 拼成 `banned=`，后端 Boolean/Integer 都解析不了空串 —— 直接摘掉这些键
      const params = { ...this.listQuery }
      const blank = ['banned', 'verifyStatus', 'gender', 'schoolId', 'keyword']
      blank.forEach(k => {
        if (params[k] === '' || params[k] === null || params[k] === undefined) {
          delete params[k]
        }
      })
      getUserList(params).then(res => {
        this.list = res.data.list || []
        this.total = res.data.total || 0
        this.listLoading = false
      }).catch(() => {
        this.listLoading = false
      })
    },
    fetchSchools() {
      // options 接口不分页；非超管拿到的天然只有自己被授权的学校
      getSchoolOptions().then(res => {
        this.schools = res.data || []
      }).catch(() => {
        this.schools = []
      })
    },
    /** 后端返回的是元，界面显示成爱豆 */
    beanOf(yuan) {
      return beanNumber(yuan)
    },
    /** 统计还没回来时占位，别闪一个假的 0 */
    statOr(v) {
      if (!this.statLoaded) return '—'
      return v === null || v === undefined ? 0 : v
    },
    txText(type) {
      return TX_TEXT[type] || type || '其他'
    },
    /** 整行可点开详情 —— 光标要给出来，否则没人知道能点 */
    rowClass() {
      return 'is-clickable'
    },
    quotaNote(used, total) {
      const u = Number(used || 0)
      const t = Number(total || 0)
      if (t === 0) return '该功能当前未开放'
      return u >= t ? '已用完，继续操作会扣爱豆' : `今天还能免费使用 ${t - u} 次`
    },
    handleFilter() {
      this.listQuery.page = 1
      this.fetchData()
    },
    resetFilter() {
      this.listQuery.keyword = ''
      this.listQuery.schoolId = ''
      this.listQuery.gender = ''
      this.listQuery.verifyStatus = ''
      this.listQuery.banned = ''
      this.handleFilter()
    },
    handleAction(cmd, row) {
      if (cmd === 'school') return this.openSchoolDialog(row)
      if (cmd === 'reset') return this.handleResetPassword(row)
      if (cmd === 'ban') return this.openBanDialog(row)
      if (cmd === 'unban') return this.handleUnban(row)
      if (cmd === 'delete') return this.handleDelete(row)
    },

    /* ==================== 详情 ====================
       打开抽屉就把这个人相关的数据一次要全：6 个接口并行发出去，
       不会出现"滚到下面才发现纸条记录还在转圈"。单个人的数据量都很小。 */
    handleDetail(row) {
      if (!row || !row.id) return
      this.detailLoading = true
      this.detailVisible = true
      this.userDetail = {}
      this.userNotes = []
      this.userTakenNotes = []
      this.userStatistics = {}
      this.statLoaded = false
      this.userReferrals = []
      this.userTransactions = []

      getUserDetail(row.id).then(res => {
        this.userDetail = res.data || {}
      }).finally(() => {
        this.detailLoading = false
      })
      this.loadProfileData(row.id)
    },
    loadProfileData(id) {
      getUserStatistics(id).then(res => {
        this.userStatistics = res.data || {}
        this.statLoaded = true
      }).catch(() => { this.statLoaded = true })

      getUserTransactions(id, { limit: 50 }).then(res => {
        const list = res.data || []
        this.userTransactions = list
      }).catch(() => {})

      getUserReferrals(id).then(res => {
        this.userReferrals = res.data || []
      }).catch(() => {})

      getUserNotes(id).then(res => {
        this.userNotes = res.data || []
      }).catch(() => {})

      getUserTakenNotes(id).then(res => {
        this.userTakenNotes = res.data || []
      }).catch(() => {})
    },
    /** 解封/改学校/改资料之后重开抽屉，旧数据必须清干净，否则会闪一下上一个人的流水 */
    onDrawerClosed() {
      this.userDetail = {}
      this.userTransactions = []
    },

    /* ==================== 封禁 ====================
       封禁 = 可逆 + 有期限 + 给用户一个解释。删除是把人抹掉，封禁是让他暂时进不来。
       两个必填项（原因、时长）都是"用户端会看到"的东西：
         reason       会原样出现在对方的登录弹窗里 —— 所以要写人话，不是内部备注
         duration     决定解封时间；永久封禁是一个明确的选择，不是"忘了填"的结果 */
    openBanDialog(row) {
      this.banForm = {
        userId: row.id,
        nickname: row.nickname || row.username || `用户 ${row.id}`,
        reason: '',
        durationHours: 24
      }
      this.banDialogVisible = true
    },
    submitBan() {
      const reason = (this.banForm.reason || '').trim()
      if (!reason) {
        this.$message.warning('请填写封禁原因，这段话会原样显示给用户')
        return
      }
      const hours = this.banForm.durationHours
      const permanent = hours === null || hours === undefined || hours <= 0
      const effect = permanent ? '该账号将被永久封禁，不会自动解封。' : `该账号将被封禁至 ${this.banUntilPreview}。`

      this.$confirm(`${effect}对方会立刻下线。`, '确认封禁', {
        type: 'warning',
        confirmButtonText: '确认封禁',
        cancelButtonText: '再想想'
      }).then(() => {
        this.banSubmitting = true
        banUser(this.banForm.userId, {
          reason,
          durationHours: permanent ? null : hours
        }).then(res => {
          this.$message.success(res.message || '已封禁')
          this.banDialogVisible = false
          // 抽屉开着的时候要从列表里刷新出最新的封禁态，否则详情上还写着"正常"
          this.refreshAfterMutation(this.banForm.userId)
        }).finally(() => {
          this.banSubmitting = false
        })
      }).catch(() => {})
    },
    handleUnban(row) {
      const who = row.nickname || row.username || `用户 ${row.id}`
      this.$confirm(`解除对「${who}」的封禁？解除后对方可以立刻登录。`, '解除封禁', {
        type: 'warning',
        confirmButtonText: '确认解封',
        cancelButtonText: '取消'
      }).then(() => {
        unbanUser(row.id).then(res => {
          this.$message.success(res.message || '已解封')
          this.refreshAfterMutation(row.id)
        })
      }).catch(() => {})
    },
    /**
     * 改动之后同步刷新"列表"和"抽屉里正在看的这个人"。
     * 只刷列表的话，管理员刚封完人，抽屉上还挂着"正常"两个字，会以为自己没点上。
     */
    refreshAfterMutation(userId) {
      this.fetchData()
      if (this.detailVisible && userId && Number(this.userDetail.id) === Number(userId)) {
        getUserDetail(userId).then(res => { this.userDetail = res.data || {} })
      }
    },

    /* ==================== 代改学校 ====================
       学生每年只能自己改一次，改完就被锁。填错、转学、学校合并这些情况，
       都得由管理端兜底 —— 这是那条限制唯一的出口。
       ⚠️ 代改不受时间限制，但会一并作废该用户的学生认证（认证跟学校绑定），
          所以确认文案必须把这一点说在前面，不能等提交完才告诉管理员。 */
    openSchoolDialog(row) {
      getUserDetail(row.id).then(res => {
        const detail = res.data || {}
        this.schoolChange = {
          userId: row.id,
          nickname: row.nickname || row.username || `用户 ${row.id}`,
          fromSchoolId: detail.schoolId || row.schoolId || null,
          fromSchoolName: detail.schoolName || row.schoolName || '未选择',
          schoolId: '',
          nextSchoolChangeAt: detail.nextSchoolChangeAt || null,
          hasVerify: detail.isVerified === 1 || (detail.verifyStatus && detail.verifyStatus !== 0)
        }
        this.schoolDialogVisible = true
      })
    },
    /** 下拉里给筹备中的学校加后缀，避免管理员改过去之后纳闷"这学校怎么没有数据" */
    schoolOptionLabel(item) {
      if (!item) return ''
      const draft = item.launchStatus === 0 || item.launchStatus === null || item.launchStatus === undefined
      return draft ? `${item.name}（筹备中，用户端不展示）` : item.name
    },
    schoolNameById(id) {
      if (!id) return ''
      const hit = (this.schools || []).find(s => s.id === id)
      return hit ? hit.name : ''
    },
    submitSchoolChange() {
      if (!this.schoolChange.schoolId) {
        this.$message.warning('请选择要改到的学校')
        return
      }
      updateUserSchool(this.schoolChange.userId, { schoolId: this.schoolChange.schoolId }).then(res => {
        this.$message.success(res.message || '修改成功')
        this.schoolDialogVisible = false
        this.refreshAfterMutation(this.schoolChange.userId)
      })
    },
    handleSizeChange(val) {
      this.listQuery.size = val
      this.fetchData()
    },
    handleCurrentChange(val) {
      this.listQuery.page = val
      this.fetchData()
    },
    handleCreate() {
      this.resetForm()
      this.dialogTitle = '新增用户'
      this.isEdit = false
      this.dialogVisible = true
    },
    handleEdit(row) {
      this.resetForm()
      this.form = { ...row }
      this.dialogTitle = '编辑用户'
      this.isEdit = true
      this.dialogVisible = true
    },
    handleDelete(row) {
      this.$confirm('确定要删除该用户吗?', '提示', {
        type: 'warning'
      }).then(() => {
        deleteUser(row.id).then(() => {
          this.$message.success('删除成功')
          if (Number(this.userDetail.id) === Number(row.id)) {
            this.detailVisible = false
          }
          this.fetchData()
        })
      })
    },
    handleResetPassword(row) {
      this.$confirm('确定要重置该用户的密码吗?', '提示', {
        type: 'warning'
      }).then(() => {
        resetUserPassword(row.id).then(res => {
          this.$message.success('新密码: ' + res.data.newPassword)
        })
      })
    },
    handleReward(row) {
      this.$prompt('请输入奖励金额', '发放奖励', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        inputPattern: /^\d+(\.\d{1,2})?$/,
        inputErrorMessage: '请输入有效的金额'
      }).then(({ value }) => {
        const amount = parseFloat(value)
        if (amount <= 0) {
          this.$message.error('奖励金额必须大于0')
          return
        }
        this.$confirm(`确定要发放 ${this.beanOf(amount)} 爱豆奖励给该推荐人吗？（¥${amount}）`, '提示', {
          type: 'warning'
        }).then(() => {
          rewardReferral({
            id: row.id,
            amount: amount
          }).then(res => {
            if (res.code === 200) {
              this.$message.success('奖励发放成功')
              row.isRewarded = 1
              row.rewardAmount = amount
            } else {
              this.$message.error(res.message || '奖励发放失败')
            }
          })
        })
      }).catch(() => {})
    },
    submitForm() {
      this.$refs.form.validate(valid => {
        if (valid) {
          if (this.isEdit) {
            const updateData = {
              id: this.form.id,
              nickname: this.form.nickname,
              gender: this.form.gender,
              email: this.form.email,
              birthDate: this.form.birthDate,
              schoolId: this.form.schoolId,
              contact: this.form.contact,
              bio: this.form.bio,
              referralCode: this.form.referralCode
            }
            updateUser(updateData).then(() => {
              this.$message.success('修改成功')
              this.dialogVisible = false
              this.refreshAfterMutation(updateData.id)
            })
          } else {
            const { id, ...createData } = this.form
            createUser(createData).then(() => {
              this.$message.success('创建成功')
              this.dialogVisible = false
              this.fetchData()
            })
          }
        }
      })
    },
    resetForm() {
      this.form = {
        id: null,
        username: '',
        nickname: '',
        password: '',
        gender: 'M',
        email: '',
        birthDate: '',
        schoolId: '',
        contact: '',
        bio: '',
        referralCode: ''
      }
    },
    formatDate(date) {
      if (!date) return ''
      return new Date(date).toLocaleString()
    }
  }
}
</script>

<style lang="scss" scoped>
.gender-select {
  width: 130px;
}

.verify-select {
  width: 140px;
}

.bean-cell {
  font-size: 13px;
  color: var(--ink-900);
}

.bean-cell b {
  font-size: 15px;
  font-weight: 600;
  color: var(--rose-600);
}

.bean-inline {
  font-size: 13px;
  font-weight: 600;
  color: var(--rose-600);
}

.row-done {
  font-size: 12.5px;
  color: var(--ink-400);
}

.photos-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 12px;
}

.photos-container .photo-item {
  border-radius: var(--r-md);
  overflow: hidden;
}

/* ---------- 表单内的字段级说明 ---------- */
.form-note {
  font-size: 12px;
  line-height: 1.6;
  color: var(--ink-400);
  margin-top: 6px;
}

/* ============================================================
   用户详情（仅本页结构，类名统一 ud- 前缀，不会外溢到别的页面）
   ============================================================ */

/* 人物 + 爱豆：一屏的视觉重心，所以这里给白卡 + 玫瑰顶条，
   其余分区一律走 .section 的轻量分隔，避免每块都"很重"。 */
.ud-hero {
  background: #fff;
  border: 1px solid var(--ink-200);
  border-top: 3px solid var(--rose-400);
  border-radius: var(--r-lg);
  padding: 18px 20px 0;
  overflow: hidden;
}

.ud-hero-top {
  display: flex;
  align-items: center;
  gap: 16px;
}

.ud-avatar {
  flex: 0 0 auto;
  background: var(--ink-100);
  color: var(--ink-500);
  font-size: 22px;
}

.ud-hero-id {
  min-width: 0;
}

.ud-name {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 17px;
  font-weight: 600;
  color: var(--ink-900);
}

.ud-handle {
  margin-top: 3px;
  font-size: 12px;
  color: var(--ink-400);
  font-family: 'SFMono-Regular', Consolas, monospace;
}

.ud-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

/* 爱豆余额：管理端核对得最多的一个数，单独占一行、字号拉开 */
.ud-bean {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  margin: 16px -20px 0;
  padding: 14px 20px;
  background: var(--paper);
  border-top: 1px solid var(--ink-200);
}

.ud-bean-main b {
  font-size: 26px;
  font-weight: 700;
  color: var(--rose-600);
  line-height: 1;
}

.ud-bean-main em {
  margin-left: 6px;
  font-style: normal;
  font-size: 12px;
  color: var(--ink-400);
}

.ud-bean-meta {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 4px 14px;
  font-size: 11.5px;
  color: var(--ink-400);
}

/* 对账提示条：紧跟在爱豆卡后面，作为它的"附注"而不是独立一屏 */
.ud-gap {
  margin-top: 10px;
}

.ud-gap b {
  font-weight: 600;
}

/* ---------- 封禁警示：比普通提示更重，因为它是有后果的状态 ---------- */
.ud-ban {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 18px;
  padding: 13px 15px;
  background: var(--rose-50);
  border-left: 3px solid var(--rose-500);
  border-radius: 0 var(--r-md) var(--r-md) 0;
}

.ud-ban > i {
  color: var(--rose-600);
  font-size: 16px;
  margin-top: 1px;
  flex-shrink: 0;
}

.ud-ban b {
  font-size: 13px;
  font-weight: 600;
  color: var(--rose-600);
}

.ud-ban-reason {
  margin: 6px 0 0;
  font-size: 12.5px;
  line-height: 1.7;
  color: var(--ink-700);
}

.ud-ban-meta {
  margin: 4px 0 0;
  font-size: 11.5px;
  color: var(--ink-400);
}

/* ---------- 学生认证 ---------- */
.ud-verify {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.ud-verify-hint {
  font-size: 12px;
  color: var(--ink-400);
}

.info-grid.is-tight {
  margin-top: 0;
}

/* ---------- 今日免费额度 ---------- */
.ud-quota {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.ud-quota-item {
  background: #fff;
  border: 1px solid var(--ink-200);
  border-radius: var(--r-md);
  padding: 13px 16px;
}

.ud-quota-label {
  font-size: 12px;
  color: var(--ink-400);
}

.ud-quota-num {
  margin-top: 6px;
}

.ud-quota-num b {
  font-size: 20px;
  font-weight: 600;
  color: var(--ink-900);
}

.ud-quota-num em {
  margin-left: 4px;
  font-style: normal;
  font-size: 12px;
  color: var(--ink-400);
}

.ud-quota-note {
  margin-top: 6px;
  font-size: 11.5px;
  color: var(--ink-400);
}

.ud-quota-tip {
  margin-top: 10px;
  font-size: 11.5px;
  line-height: 1.7;
  color: var(--ink-350);
}

/* ---------- 爱豆明细 ---------- */
.ud-tx {
  background: #fff;
  border: 1px solid var(--ink-200);
  border-radius: var(--r-md);
  overflow: hidden;
}

.ud-tx-row {
  display: grid;
  grid-template-columns: 1fr 96px 150px;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
}

.ud-tx-row + .ud-tx-row {
  border-top: 1px solid var(--ink-100);
}

.ud-tx-main {
  min-width: 0;
}

.ud-tx-type {
  font-size: 13px;
  color: var(--ink-900);
}

.ud-tx-desc {
  display: block;
  margin-top: 2px;
  font-size: 11.5px;
  color: var(--ink-400);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ud-tx-amount {
  text-align: right;
  font-size: 14px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

/* 进账用品牌玫瑰，出账保持墨色 —— 支出是正常行为，不该被标成"异常" */
.ud-tx-amount.is-in {
  color: var(--rose-600);
}

.ud-tx-amount.is-out {
  color: var(--ink-700);
}

.ud-tx-time {
  text-align: right;
  font-size: 11.5px;
  color: var(--ink-400);
}

.ud-tx-more,
.ud-list-more {
  padding: 9px 14px;
  font-size: 11.5px;
  color: var(--ink-350);
  background: var(--paper);
  border-top: 1px solid var(--ink-100);
}

/* ---------- 纸条 / 邀请：同一种"列表行"骨架 ---------- */
.ud-list {
  background: #fff;
  border: 1px solid var(--ink-200);
  border-radius: var(--r-md);
  overflow: hidden;
}

.ud-list-row {
  padding: 11px 14px;
}

.ud-list-row + .ud-list-row {
  border-top: 1px solid var(--ink-100);
}

.ud-list-row.is-ref {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.ud-list-main {
  min-width: 0;
}

.ud-list-text {
  font-size: 13px;
  color: var(--ink-900);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.ud-list-meta {
  margin-top: 4px;
  font-size: 11.5px;
  color: var(--ink-400);
}

.ud-list-meta-inline {
  margin-left: 8px;
  font-size: 11.5px;
  color: var(--ink-400);
}

.ud-list-side {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

/* ---------- 拉新关系 ---------- */
.ud-ref-line {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 14px;
  background: #fff;
  border: 1px solid var(--ink-200);
  border-radius: var(--r-md);
  font-size: 12.5px;
}

.ud-ref-line + .ud-ref-line {
  margin-top: 8px;
}

.ud-ref-label {
  flex: 0 0 auto;
  width: 84px;
  color: var(--ink-400);
  font-size: 12px;
}

.ud-ref-value {
  color: var(--ink-900);
}

.ud-ref-value.is-muted {
  color: var(--ink-300);
}

.ud-ref-id {
  margin-left: 6px;
  font-size: 11.5px;
  color: var(--ink-400);
}

.ud-ref-line + .ud-list {
  margin-top: 12px;
}

/* ---------- 修改学校对话框（仅本页结构） ---------- */
.school-change__who {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 16px;
}

.school-change__name {
  font-size: 14px;
  font-weight: 600;
  color: var(--ink-900);
}

.school-change__id {
  font-size: 12px;
  color: var(--ink-400);
}

.school-change__flow {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  background: var(--paper);
  border: 1px solid var(--line);
  border-radius: var(--r-md);
  margin-bottom: 14px;
}

.school-change__side {
  flex: 1;
  min-width: 0;
}

.school-change__label {
  font-size: 11.5px;
  color: var(--ink-400);
  margin-bottom: 5px;
}

.school-change__school {
  font-size: 13px;
  font-weight: 600;
  color: var(--ink-900);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.school-change__school.is-empty {
  color: var(--ink-300);
  font-weight: 400;
}

.school-change__arrow {
  color: var(--ink-300);
  font-size: 15px;
}

.school-change__warn {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 14px;
  padding: 11px 13px;
  background: var(--rose-50);
  border-left: 3px solid var(--rose-500);
  border-radius: 0 var(--r-sm) var(--r-sm) 0;
  font-size: 12.5px;
  line-height: 1.7;
  color: var(--ink-700);
}

.school-change__warn i {
  color: var(--rose-600);
  font-size: 15px;
  margin-top: 1px;
  flex-shrink: 0;
}

.school-change__warn b {
  color: var(--rose-600);
  font-weight: 600;
}

.school-change__meta {
  margin-top: 14px;
  font-size: 12px;
  line-height: 1.7;
  color: var(--ink-400);
}

.school-change__next {
  display: block;
  margin-top: 4px;
  color: var(--ink-500);
}

.status-select {
  width: 130px;
}

/* ---------- 封禁对话框（仅本页结构） ---------- */
.ban-box__who {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 18px;
}

.ban-box__name {
  font-size: 14px;
  font-weight: 600;
  color: var(--ink-900);
}

.ban-box__id {
  font-size: 12px;
  color: var(--ink-400);
}

.ban-field + .ban-field {
  margin-top: 18px;
}

.ban-field__label {
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: var(--ink-900);
  margin-bottom: 8px;
}

.ban-field__hint {
  font-size: 11.5px;
  font-weight: 400;
  color: var(--ink-400);
}

/* 时长 chips：与动态置顶弹窗同一形态，管理员不用重新学一遍 */
.chip-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.chip {
  font-size: 13px;
  padding: 5px 14px;
  border-radius: 999px;
  border: 1px solid var(--ink-200);
  color: var(--ink-500);
  cursor: pointer;
  transition: all 0.16s;
}

.chip:hover {
  border-color: var(--ink-300);
}

.chip.is-on {
  background: var(--ink-900);
  border-color: var(--ink-900);
  color: #fff;
}

.custom-dur {
  display: flex;
  align-items: center;
  margin-top: 12px;
}

.custom-dur__unit {
  font-size: 13px;
  color: var(--ink-400);
  margin-left: 8px;
}

/* 后果条：封完会发生什么，点确认之前就说清楚 */
.ban-box__effect {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 20px;
  padding: 11px 13px;
  background: var(--rose-50);
  border-left: 3px solid var(--rose-500);
  border-radius: 0 var(--r-sm) var(--r-sm) 0;
  font-size: 12.5px;
  line-height: 1.7;
  color: var(--ink-700);
}

.ban-box__effect i {
  color: var(--rose-600);
  font-size: 15px;
  margin-top: 1px;
  flex-shrink: 0;
}

.ban-box__effect b {
  color: var(--rose-600);
  font-weight: 600;
}
</style>
